// Constants
const ON_CHANGE_DEBOUNCE_TIMER = 300;

const PUB_SUB_EVENTS = {
  cartUpdate: 'cart-update',
  quantityUpdate: 'quantity-update',
  optionValueSelectionChange: 'option-value-selection-change',
  variantChange: 'variant-change',
  cartError: 'cart-error',
};

// PubSub
let subscribers = {};

function subscribe(eventName, callback) {
  if (subscribers[eventName] === undefined) {
    subscribers[eventName] = [];
  }

  subscribers[eventName] = [...subscribers[eventName], callback];

  return function unsubscribe() {
    subscribers[eventName] = subscribers[eventName].filter((cb) => {
      return cb !== callback;
    });
  };
}

// global functions

function publish(eventName, data) {
  if (subscribers[eventName]) {
    const promises = subscribers[eventName].map((callback) => callback(data));
    return Promise.all(promises);
  }
}

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }
  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });
  if (summary.hasAttribute('hoverenable')) {
    summary.addEventListener('mouseenter', () => {
      summary.focus();
      summary.click();
    });
    summary.parentElement.addEventListener('mouseleave', () => {
      summary.click();
      summary.blur();
    });
  }
  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (this.classList.contains('media-modal--gallery')) {
          return;
        }
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) {
          this.hide();
        }
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class VideoList extends HTMLElement {
  connectedCallback() {
    const thumbnails = this.querySelectorAll('.video-thumbnail-list__item');
    for (const thumbnail of thumbnails) {
      thumbnail.addEventListener('click', this.onThumbnailClick.bind(this));
    }
    this.onResize();
    window.addEventListener('resize', this.onResize.bind(this));
  }
  onResize() {
    const deferredMedia = this.querySelector('deferred-media:not([hidden])');
    const height = deferredMedia.clientHeight;
    const thumbnailListEl = this.querySelector('.video-thumbnail-list');
    thumbnailListEl.style.height = `${height}px`;
  }
  onThumbnailClick(event) {
    window.pauseAllMedia();
    const videoId = event.currentTarget.dataset.videoId;
    const deferredMediaEls = this.querySelectorAll('deferred-media');
    for (const deferredMedia of deferredMediaEls) {
      if (deferredMedia.getAttribute('data-media-id') === videoId) {
        deferredMedia.removeAttribute('hidden');
      } else {
        deferredMedia.setAttribute('hidden', '');
      }
    }
  }
}

customElements.define('video-list', VideoList);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.sliderItemOffset = 50;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }
  connectedCallback() {
    const desktopAutoplay = this.slider.getAttribute('autoplay-desktop') === 'true';
    const mobileAutoplay = this.slider.getAttribute('autoplay-mobile') === 'true';
    if (
      (desktopAutoplay && document.documentElement.clientWidth > 768) ||
      (mobileAutoplay && document.documentElement.clientWidth <= 768)
    ) {
      window.requestAnimationFrame(this.autoplay.bind(this));
    }
  }
  autoplay() {
    const position = Number(this.slider.dataset.position || 5);
    let direction = this.slider.dataset.direction || 'start';
    const currentWidth = this.slider.clientWidth + (this.slider.clientWidth * position) / 10000;
    this.slider.style.transform = `translateX(-${position / 100}%)`;
    if (direction === 'start' && currentWidth > this.slider.scrollWidth) {
      this.slider.dataset.direction = 'end';
      direction = 'end';
    } else if (direction === 'end' && position === 5) {
      this.slider.dataset.direction = 'start';
      direction = 'start';
    }

    if (direction === 'end') {
      this.slider.dataset.position = position - 5;
    } else {
      this.slider.dataset.position = position + 5;
    }
    window.requestAnimationFrame(this.autoplay.bind(this));

    return;
  }
  get isProductThumbnailSlider() {
    return this.dataset.isProductThumbnailSlider === 'true';
  }
  initActiveSlideItem() {
    const activeSlideItem = this.querySelector('[id^="Slide-"]:not([hidden])');
    if (!activeSlideItem || activeSlideItem.classList.contains('is-active')) {
      return;
    }
    this.sliderItems.forEach((slideItem) => {
      if (slideItem.id === activeSlideItem.id) {
        slideItem.classList.add('is-active');
      } else {
        slideItem.classList.remove('is-active');
      }
    });
  }
  initPages() {
    // this.initActiveSlideItem();
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    if (document.documentElement.clientWidth > 990 && this.isProductThumbnailSlider) {
      this.sliderItemOffset = this.sliderItemsToShow[1].offsetTop - this.sliderItemsToShow[0].offsetTop;
      this.slidesPerPage = Math.floor(
        (this.slider.clientHeight - this.sliderItemsToShow[0].offsetTop) / this.sliderItemOffset
      );
    } else {
      this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
      this.slidesPerPage = Math.floor(
        (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
      );
    }
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;
    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider[this.scrollKey] === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }
    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    if (document.documentElement.clientWidth > 990 && this.isProductThumbnailSlider) {
      const lastVisibleSlide = this.slider.clientHeight + this.slider.scrollTop - offset;
      return element.offsetTop + element.clientHeight <= lastVisibleSlide && element.offsetTop >= this.slider.scrollTop;
    } else {
      const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
      return (
        element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft
      );
    }
  }
  get scrollKey() {
    return document.documentElement.clientWidth > 990 && this.isProductThumbnailSlider ? 'scrollTop' : 'scrollLeft';
  }
  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    const offset = Number(step) * this.sliderItemOffset;

    if (document.documentElement.clientWidth > 990 && this.isProductThumbnailSlider) {
      this.slideScrollPosition =
        event.currentTarget.name === 'next' ? this.slider.scrollTop + offset : this.slider.scrollTop - offset;
      this.slider.scrollTo({
        top: this.slideScrollPosition,
      });
    } else {
      this.slideScrollPosition =
        event.currentTarget.name === 'next' ? this.slider.scrollLeft + offset : this.slider.scrollLeft - offset;
      this.setSlidePosition(this.slideScrollPosition);
    }
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = 'cart-performance';

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp,
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(benchmarkName, `${metricName}:start`, `${metricName}:end`);
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(benchmarkName, startMarker.name, `${metricName}:end`);
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(benchmarkName, `${metricName}:start`, `${metricName}:end`);
  }
}

class CollapsibleElement extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.querySelector('.collapsible-element__toggle')?.addEventListener('click', () => {
      if (this.getAttribute('collapsed') === '') {
        this.removeAttribute('collapsed');
        this.setAttribute('expanded', '');
      } else {
        this.removeAttribute('expanded');
        this.setAttribute('collapsed', '');
        this.querySelectorAll('video').forEach((video) => {
          video.pause();
        });
      }
    });
  }

  open() {
    this.removeAttribute('collapsed');
    this.setAttribute('expanded', '');
  }
}
if (!customElements.get('collapsible-element')) {
  customElements.define('collapsible-element', CollapsibleElement);
}

class SwiperContainerWrapper extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.swiperContainer = this.querySelector('swiper-container');
    if (this.swiperContainer) {
      this.swiperContainer.addEventListener('swiperslidechange', () => {
        if (this.swiperContainer.swiper.activeIndex === 0) {
          this.querySelector('.swiper-button-prev').setAttribute('disabled', '');
        } else {
          this.querySelector('.swiper-button-prev').removeAttribute('disabled');
        }
        if (this.swiperContainer.swiper.activeIndex === this.swiperContainer.swiper.slides.length - 1) {
          this.querySelector('.swiper-button-next').setAttribute('disabled', '');
        } else {
          this.querySelector('.swiper-button-next').removeAttribute('disabled');
        }
        for (let i = 0; i < this.swiperContainer.swiper.slides.length; i++) {
          const slide = this.swiperContainer.swiper.slides[i];
          const videoEl = slide.querySelector('video');
          if (videoEl) {
            if (i !== this.swiperContainer.swiper.activeIndex) {
              videoEl.pause();
              videoEl.currentTime = 0;
            } else {
              videoEl.play();
            }
          }
        }
      });
      this.querySelector('.swiper-button-next').addEventListener('click', () => {
        this.swiperContainer.swiper.slideNext();
      });
      this.querySelector('.swiper-button-prev').addEventListener('click', () => {
        this.swiperContainer.swiper.slidePrev();
      });
    }
  }
}
if (!customElements.get('swiper-container-wrapper')) {
  customElements.define('swiper-container-wrapper', SwiperContainerWrapper);
}

class AnchorBar extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const header = document.querySelector('header.header');
    if (header) {
      setTimeout(() => {
        this.parentElement.style.top = `${
          document.body.clientWidth > 768 ? header.clientHeight : header.clientHeight - this.clientHeight
        }px`;
      }, 0);
    }
    const container = this.querySelector('.anchor-bar__container');
    while (container.children.length > 0) {
      container.removeChild(container.children[0]);
    }
    const anchorPoints = Array.from(document.querySelectorAll('[anchor-points], #judgeme_product_reviews'));
    for (let i = 0; i < anchorPoints.length; i++) {
      const anchorPoint = anchorPoints[i];
      let heading = '';
      if (anchorPoint.id === 'judgeme_product_reviews') {
        heading = 'Reviews';
      } else {
        heading = anchorPoint.getAttribute('anchor-heading');
      }
      const el = document.createElement('div');
      el.innerHTML = `<div class="anchor-bar__item-text">${heading}</div>`;
      el.setAttribute('data-anchor-point-id', anchorPoint.id);
      el.classList.add('anchor-bar__item');
      if (i === 0) {
        el.classList.add('anchor-bar__item--active');
      }
      el.setAttribute('anchor-point-heading', heading);
      el.addEventListener('click', (event) => {
        event.target.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });

        const anchorPoint = document.getElementById(event.currentTarget.dataset.anchorPointId);
        history.pushState({}, '', `${window.location.pathname}#${anchorPoint.id}`);
        const anchorPointTop = anchorPoint.getBoundingClientRect().top;
        const headerHeight = document.querySelector('header.header').clientHeight;
        const productBar = document.querySelector('.product-bar');
        let scrollTop = anchorPointTop + window.scrollY - headerHeight;
        if (productBar) {
        } else {
          scrollTop -= productBar.clientHeight;
        }
        window.scrollTo({ top: scrollTop, behavior: 'smooth' });
        this.querySelector('.anchor-bar__item--active').classList.remove('anchor-bar__item--active');
        el.classList.add('anchor-bar__item--active');

        const collapsibleElement = anchorPoint.querySelector('collapsible-element');
        if (collapsibleElement) {
          collapsibleElement.open();
        }
      });
      container.appendChild(el);
    }
    let scrollTimer = null;
    window.addEventListener('scroll', (event) => {
      if (scrollTimer) {
        clearTimeout(scrollTimer);
      }

      scrollTimer = setTimeout(
        () => {
          const anchorBarItems = Array.from(this.querySelectorAll('.anchor-bar__item'));
          // 获取最接近当前视图的锚点
          let matchedAnchorBarItem = null;
          let matchedAnchorPointTop = null;
          for (let i = 0; i < anchorBarItems.length; i++) {
            const anchorBarItem = anchorBarItems[i];
            const anchorPoint = document.getElementById(anchorBarItem.getAttribute('data-anchor-point-id'));
            const anchorPointTop = anchorPoint.getBoundingClientRect().top;
            if (matchedAnchorPointTop === null) {
              matchedAnchorPointTop = anchorPointTop;
              matchedAnchorBarItem = anchorBarItem;
            } else if (
              Math.abs(Math.abs(anchorPointTop) - this.clientHeight) <
              Math.abs(Math.abs(matchedAnchorPointTop) - this.clientHeight)
            ) {
              matchedAnchorBarItem = anchorBarItem;
              matchedAnchorPointTop = anchorPointTop;
            }
          }
          if (!matchedAnchorBarItem.classList.contains('anchor-bar__item--active')) {
            anchorBarItems.forEach((item) => {
              item.classList.remove('anchor-bar__item--active');
            });
            matchedAnchorBarItem.classList.add('anchor-bar__item--active');
            if (window.innerWidth <= 768) {
              matchedAnchorBarItem?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
            }
          }
        },
        document.body.clientWidth > 768 ? 0 : 50
      );
    });
  }
}

if (!customElements.get('anchor-bar')) {
  customElements.define('anchor-bar', AnchorBar);
}

// ... existing code ...

class RecentlyViewedProducts extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.loadRecentlyViewedProducts();
  }

  async loadRecentlyViewedProducts() {
    if (this.searchQueryString === '') {
      return;
    }
    const response = await fetch(
      `${window.Shopify.routes.root}search?type=product&q=${this.searchQueryString}&section_id=${this.dataset.sectionId}`
    );
    const div = document.createElement('div');
    div.innerHTML = await response.text();
    const recentlyViewedProductsElement = div.querySelector('recently-viewed-products');
    if (recentlyViewedProductsElement.hasChildNodes()) {
      this.innerHTML = recentlyViewedProductsElement.innerHTML;
    }
  }

  get searchQueryString() {
    const items = JSON.parse(localStorage.getItem('theme:recently-viewed-products') || '[]');
    if (this.hasAttribute('exclude-product-id') && items.includes(parseInt(this.getAttribute('exclude-product-id')))) {
      items.splice(items.indexOf(parseInt(this.getAttribute('exclude-product-id'))), 1);
    }
    return items
      .map((item) => 'id:' + item)
      .slice(0, this.productsCount)
      .join(' OR ');
  }
  get sectionId() {
    return this.getAttribute('section-id');
  }
  get productsCount() {
    return this.getAttribute('products-count') || 4;
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}

if (!window.isListenRecentlyViewedProducts) {
  window.isListenRecentlyViewedProducts = true;
  window.addEventListener('DOMContentLoaded', () => {
    const productDetail = document.querySelector('[data-is-product-detail]');
    if (productDetail) {
      const productId = productDetail.dataset.productId;
      try {
        const viewedProducts = JSON.parse(localStorage.getItem('theme:recently-viewed-products') || '[]').filter(
          (item) => item !== productId
        );
        viewedProducts.unshift(productId);
        localStorage.setItem('theme:recently-viewed-products', JSON.stringify(viewedProducts));
      } catch (error) {
        localStorage.setItem('theme:recently-viewed-products', JSON.stringify([productId]));
      }
    }
  });
}

class RichTextCollapsible extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const moreEl = this.querySelector('.rich-text__collapsible-more');
    const lessEl = this.querySelector('.rich-text__collapsible-less');
    if (moreEl && lessEl) {
      if (
        this.querySelector('.rich-text__text').clientHeight < this.querySelector('.rich-text__raw-text').clientHeight
      ) {
        moreEl.classList.remove('hidden');
      }
      moreEl.addEventListener('click', () => {
        moreEl.classList.add('hidden');
        lessEl.classList.remove('hidden');
        this.querySelector('.rich-text__text').style.setProperty('--lines', 999);
      });
      lessEl.addEventListener('click', () => {
        lessEl.classList.add('hidden');
        moreEl.classList.remove('hidden');
        this.querySelector('.rich-text__text').style.setProperty('--lines', this.dataset.lines);
      });
    }
  }
}

if (!customElements.get('rich-text-collapsible')) {
  customElements.define('rich-text-collapsible', RichTextCollapsible);
}

class FaqNavigation extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    const items = this.querySelectorAll('.faq-navigation_item');
    items.forEach((item) => {
      item.addEventListener('click', () => {
        items.forEach((item) => item.classList.remove('active'));
        item.classList.add('active');
        const groups = document.querySelectorAll('.faq-groups');
        groups.forEach((group) => {
          if (group.id === item.getAttribute('aria-controls')) {
            group.classList.remove('hidden');
          } else {
            group.classList.add('hidden');
          }
        });
      });
    });
  }
}

if (!customElements.get('faq-navigation')) {
  customElements.define('faq-navigation', FaqNavigation);
}

class ToTop extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    this.lastScrollY = window.scrollY;
    this.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
    this.unsubscribeScroll = window.addEventListener('scroll', () => {
      if (window.scrollY < 150 || window.scrollY >= this.lastScrollY) {
        // scroll down
        this.lastScrollY = window.scrollY;
        this.classList.remove('to-top--visible');
      } else {
        // scroll up
        this.classList.add('to-top--visible');
        this.lastScrollY = window.scrollY;
      }
    });
  }
  disconnectedCallback() {
    this.unsubscribeScroll();
  }
}

if (!customElements.get('to-top')) {
  customElements.define('to-top', ToTop);
}

class CardProduct extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    const colorSwatches = this.querySelectorAll('.card__color-swatch-item');
    colorSwatches.forEach((colorSwatch) => {
      colorSwatch.addEventListener('click', (event) => {
        const currentColor = event.currentTarget.dataset.color;
        colorSwatches.forEach((colorSwatch) => {
          if (colorSwatch.dataset.color === currentColor) {
            colorSwatch.setAttribute('active', '');
          } else {
            colorSwatch.removeAttribute('active');
          }
        });
        const matchedMediaEl = this.querySelector(`.media[data-color="${currentColor}"]`);
        if (matchedMediaEl) {
          this.querySelectorAll('.media').forEach((mediaEl) => {
            if (mediaEl.dataset.color === currentColor) {
              mediaEl.classList.remove('hidden');
            } else {
              mediaEl.classList.add('hidden');
            }
          });
        }
      });
    });

    if (colorSwatches.length === 0) {
      return;
    }
    const selectedSwatch = this.querySelector('.card__color-swatch-item[active]');
    if (selectedSwatch && selectedSwatch.dataset.color) {
      const matchedMediaEl = this.querySelector(`.media[data-color="${selectedSwatch.dataset.color}"]`);
      if (matchedMediaEl) {
        this.querySelectorAll('.media').forEach((mediaEl) => {
          if (mediaEl.dataset.color === selectedSwatch.dataset.color) {
            mediaEl.classList.remove('hidden');
          } else {
            mediaEl.classList.add('hidden');
          }
        });
      }
    }
  }
}

if (!customElements.get('card-product')) {
  customElements.define('card-product', CardProduct);
}

class ImageVr360 extends HTMLElement {
  connectedCallback() {
    const imageUrl = this.dataset.imageUrl;
    // const alt = this.dataset.alt;
    const id = this.dataset.id;
    setTimeout(() => {
      pannellum.viewer(id, {
        type: 'equirectangular',
        panorama: imageUrl,
        autoLoad: true,
        draggable: true,
      });
    }, 1000);
    this.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
    });
  }
}

if (!customElements.get('image-vr-360')) {
  customElements.define('image-vr-360', ImageVr360);
}

class BlogTags extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    const items = this.querySelectorAll('.blog-tag');
    console.log('items', items);
    items.forEach((item) => {
      item.addEventListener('click', this.changeTag.bind(this));
    });
  }
  changeTag(e) {
    console.log('changeTag', e.currentTarget);
    const tag = e.currentTarget.dataset.tag;
    const currentTag = this.querySelector('.blog-tag[aria-selected="true"]');
    if (currentTag) {
      currentTag.removeAttribute('aria-selected');
    }
    e.currentTarget.setAttribute('aria-selected', 'true');
    const articleEls = document.querySelectorAll('.blog-articles__article');
    articleEls.forEach((articleEl) => {
      const tags = articleEl.dataset.tag.split(',');
      if (!tag || tags.includes(tag)) {
        articleEl.classList.remove('hidden');
      } else {
        articleEl.classList.add('hidden');
      }
    });
  }
}

if (!customElements.get('blog-tags')) {
  customElements.define('blog-tags', BlogTags);
}

class IconWithTexts extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    this.timer = null;
    this.swiperContainer = this.querySelector('swiper-container');
    this.unsubscribeResize = window.addEventListener('resize', () => {
      this.updateSwiperContainer();
    });
    this.updateSwiperContainer();
  }
  disconnectedCallback() {
    this.unsubscribeResize();
  }
  updateSwiperContainer() {
    if (window.innerWidth > 768 && window.innerWidth <= 1240) {
      this.swiperContainer.setAttribute('slides-per-view', 2);
      this.swiperContainer.setAttribute('autoplay', 'true');
      this.swiperContainer.setAttribute('autoplay-delay', 7000);
      this.swiperContainer.setAttribute('slides-per-group', 2);
    } else if (window.innerWidth > 1240) {
      this.swiperContainer.setAttribute('slides-per-view', 4);
      this.swiperContainer.setAttribute('slides-per-group', 4);
      this.swiperContainer.removeAttribute('autoplay');
      this.swiperContainer.removeAttribute('autoplay-delay');
    } else {
      this.swiperContainer.setAttribute('slides-per-view', 1);
      this.swiperContainer.setAttribute('autoplay', 'true');
      this.swiperContainer.setAttribute('autoplay-delay', 7000);
      this.swiperContainer.setAttribute('slides-per-group', 1);
    }
  }
}

if (!customElements.get('icon-with-texts')) {
  customElements.define('icon-with-texts', IconWithTexts);
}
class StickyHeader extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = document.querySelector('.section-header');
    this.headerIsAlwaysSticky =
      this.getAttribute('data-sticky-type') === 'always' ||
      this.getAttribute('data-sticky-type') === 'reduce-logo-size';
    this.headerBounds = {};

    this.setHeaderHeight();

    window.matchMedia('(max-width: 990px)').addEventListener('change', this.setHeaderHeight.bind(this));

    if (this.headerIsAlwaysSticky) {
      this.header.classList.add('shopify-section-header-sticky');
    }

    this.currentScrollTop = 0;
    this.preventReveal = false;
    this.predictiveSearch = this.querySelector('predictive-search');

    this.onScrollHandler = this.onScroll.bind(this);
    this.hideHeaderOnScrollUp = () => (this.preventReveal = true);

    this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
    window.addEventListener('scroll', this.onScrollHandler, false);

    this.createObserver();
  }

  setHeaderHeight() {
    document.documentElement.style.setProperty('--header-height', `${this.header.offsetHeight}px`);
  }

  disconnectedCallback() {
    this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
    window.removeEventListener('scroll', this.onScrollHandler);
  }

  createObserver() {
    let observer = new IntersectionObserver((entries, observer) => {
      this.headerBounds = entries[0].intersectionRect;
      observer.disconnect();
    });

    observer.observe(this.header);
  }

  onScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    if (this.predictiveSearch && this.predictiveSearch.isOpen) return;

    if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
      this.header.classList.add('scrolled-past-header');
      if (this.preventHide) return;
      requestAnimationFrame(this.hide.bind(this));
    } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
      this.header.classList.add('scrolled-past-header');
      if (!this.preventReveal) {
        requestAnimationFrame(this.reveal.bind(this));
      } else {
        window.clearTimeout(this.isScrolling);

        this.isScrolling = setTimeout(() => {
          this.preventReveal = false;
        }, 66);

        requestAnimationFrame(this.hide.bind(this));
      }
    } else if (scrollTop <= this.headerBounds.top) {
      this.header.classList.remove('scrolled-past-header');
      requestAnimationFrame(this.reset.bind(this));
    }

    this.currentScrollTop = scrollTop;
  }

  hide() {
    if (this.headerIsAlwaysSticky) return;
    this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
    this.closeMenuDisclosure();
    this.closeSearchModal();
  }

  reveal() {
    if (this.headerIsAlwaysSticky) return;
    this.header.classList.add('shopify-section-header-sticky', 'animate');
    this.header.classList.remove('shopify-section-header-hidden');
  }

  reset() {
    if (this.headerIsAlwaysSticky) return;
    this.header.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
  }

  closeMenuDisclosure() {
    this.disclosures = this.disclosures || this.header.querySelectorAll('header-menu');
    this.disclosures.forEach((disclosure) => disclosure.close());
  }

  closeSearchModal() {
    this.searchModal = this.searchModal || this.header.querySelector('details-modal');
    this.searchModal?.close?.(false);
  }
}

if (!customElements.get('sticky-header')) {
  customElements.define('sticky-header', StickyHeader);
}

(() => {
  // 只把「合法的千分位分组」当成一个数（10,000 / 1,000,000）；
  // 2,5 / 4,9 / 1,50 € 这类是欧陆写法的小数逗号，不能吞掉，退回只取整数段。
  // 该正则的匹配【起点】与原来的 /\d+/ 恒等，只在真正的千分位上向右多吞。
  const NUMBER_PATTERN = /\d{1,3}(?:,\d{3})+(?!\d)|\d+/;
  const STEPS = 11;
  const TICK = 60;
  const timers = new WeakMap();

  // 全主题其它动画都在 prefers-reduced-motion 守卫之后（animations.js:43 等），
  // 只有这段计数以前是裸奔的。
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return;
  }

  const format = (value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  const stop = (element) => {
    const timer = timers.get(element);
    if (timer) {
      clearInterval(timer);
      timers.delete(element);
    }
  };

  const start = (element) => {
    const source = element.dataset.number || '';
    const matched = source.match(NUMBER_PATTERN);
    // 标题里没有数字时（例如商家给纯文字标题勾了这个开关）直接跳过。
    // 原实现在这里解构 null 会抛 TypeError，把后面所有数字的动画一起带死。
    if (!matched) {
      return;
    }
    const target = Number(matched[0].replace(/,/g, ''));
    if (!Number.isFinite(target)) {
      return;
    }
    // 按匹配【位置】切分前后缀，而不是把当前值 replace 回原串，
    // 这样 "$1,000+" 的 $ 和 + 不会被动到，也不会去反读 textContent。
    const prefix = source.slice(0, matched.index);
    const suffix = source.slice(matched.index + matched[0].length);

    stop(element);
    let current = 0;
    element.textContent = prefix + format(current) + suffix;
    if (current >= target) {
      return;
    }

    const timer = setInterval(() => {
      // 每步至少 +1：target <= 5 时 Math.round(target / 11) 为 0，
      // 原实现会永远停在 0，计时器也就永远不会被清掉。
      // 对 target >= 6，Math.max 是数学恒等空操作，逐帧序列与原来完全一致。
      current = Math.min(target, Math.max(current + 1, Math.round(current + target / STEPS)));
      element.textContent = prefix + format(current) + suffix;
      if (current >= target) {
        stop(element);
      }
    }, TICK);
    timers.set(element, timer);
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) {
        entry.target.setAttribute('in-view', 'false');
        stop(entry.target); // 原实现在这里不清定时器，会叠加出多个并发 interval
        return;
      }
      if (entry.target.getAttribute('in-view') === 'true') {
        return;
      }
      entry.target.setAttribute('in-view', 'true');
      start(entry.target);
    });
  });

  const observe = (root) => {
    root.querySelectorAll('[is-a-number="true"]').forEach((element) => observer.observe(element));
  };

  observe(document);

  // 编辑器里 section 重渲染后，原实现的静态 NodeList 会失效（新元素永远不动画）。
  // 对齐 assets/animations.js:99-101 的既有范式。
  if (Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => observe(event.target));
    document.addEventListener('shopify:section:unload', (event) => {
      event.target.querySelectorAll('[is-a-number="true"]').forEach((element) => {
        stop(element);
        observer.unobserve(element);
      });
    });
  }
})();
